import * as React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import {
  Box, Grid, Dialog,
} from '@material-ui/core';
import MetalBinLookup from './MetalBinLookup';
import MetalBinAdd from './MetalBinAdd';
import type { IMetalBin } from './MetalBinInterfaces';

const MetalBins: React.FC<unknown> = () => {
  const [upsertingBin, setUpsertingBin] = React.useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  const fetcher = (url: string) => axios.get<IMetalBin[]>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching metal bins:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });
  const { data, mutate } = useSWR(['/metalBin/GetBins'], (url) => fetcher(url));
  const [metalBinFormValues, setMetalBinFormValues] = React.useState<IMetalBin>({
    metalBinName: null,
    metalType: null,
    ringThickness: null,
    ringWidth: null,
    sizeChange: null,
  });

  const [showModal, setShowModal] = React.useState(false);

  const [editMetalBinFormValues, setEditMetalBinFormValues] = React.useState<IMetalBin>({
    metalBinName: null,
    metalType: null,
    ringThickness: null,
    ringWidth: null,
    sizeChange: null,
  });

  const resetState = () => {
    setMetalBinFormValues({
      metalBinName: null,
      metalType: null,
      ringThickness: null,
      ringWidth: null,
      sizeChange: null,
    });
  };

  const handleAddBinBtnClick = () => {
    if (!metalBinFormValues.metalBinName || !metalBinFormValues.metalType || !metalBinFormValues.ringThickness || !metalBinFormValues.ringWidth || !metalBinFormValues.sizeChange) {
      enqueueSnackbar('Please fill out all the fields for the bin you are trying to create', { persist: false, variant: 'error' });
    } else {
      setUpsertingBin(true);
      axios.post<IMetalBin>('/metalBin/upsert', metalBinFormValues)
        .then(() => {
          enqueueSnackbar('Metal bin added.', { variant: 'success' });
          setUpsertingBin(false);
          mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
          resetState();
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when adding metal bin:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
          setUpsertingBin(false);
        });
    }
  };

  const handleEditBinBtnClick = () => {
    if (!editMetalBinFormValues.metalBinName || !editMetalBinFormValues.metalType || !editMetalBinFormValues.ringThickness || !editMetalBinFormValues.ringWidth || !editMetalBinFormValues.sizeChange) {
      enqueueSnackbar('Please fill out all the fields for the bin you are trying to edit', { persist: false, variant: 'error' });
    } else {
      setUpsertingBin(true);
      if (editMetalBinFormValues?.id) {
        axios.post<IMetalBin>('/metalBin/upsert', editMetalBinFormValues)
          .then(() => {
            enqueueSnackbar('Metal bin edited.', { variant: 'success' });
            setUpsertingBin(false);
            mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
            resetState();
            setShowModal(false);
          })
          .catch((e: AxiosError) => {
            enqueueSnackbar(`There was the following error when editing metal bin:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
            setUpsertingBin(false);
          });
      } else {
        enqueueSnackbar('There is no metal bin selected to edit');
      }
    }
  };

  const handleDeleteButton = () => {
    if (editMetalBinFormValues?.id) {
      axios.post<IMetalBin>('/metalBin/delete', { id: editMetalBinFormValues.id })
        .then(() => {
          enqueueSnackbar('Metal bin deleted.', { variant: 'success' });
          mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when deleting metal bin:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        });
    } else {
      enqueueSnackbar('There is no metal bin selected to delete');
    }
  };

  const editCancelButton = () => {
    setEditMetalBinFormValues({
      metalBinName: null,
      metalType: null,
      ringThickness: null,
      ringWidth: null,
      sizeChange: null,
    });
    setShowModal(false);
  };

  return (
    <Grid xs={12} md={8}>
      <Grid item>
        <Box py={1}>
          <MetalBinAdd
            formValues={metalBinFormValues}
            setFormValues={setMetalBinFormValues}
            secondaryButtonAction={resetState}
            primaryButtonAction={handleAddBinBtnClick}
            interfaceType="Add"
            loading={upsertingBin}
          />
        </Box>
      </Grid>
      <Grid item>
        <Box py={1}>
          <MetalBinLookup
            data={data || undefined}
            setShowModal={setShowModal}
            setEditMetalBinFormValues={setEditMetalBinFormValues}
            deleteMetalBin={handleDeleteButton}
          />
        </Box>
      </Grid>
      <Dialog
        onClose={editCancelButton}
        open={showModal}
        fullWidth
        maxWidth="md"
      >
        <MetalBinAdd
          formValues={editMetalBinFormValues}
          setFormValues={setEditMetalBinFormValues}
          secondaryButtonAction={editCancelButton}
          primaryButtonAction={handleEditBinBtnClick}
          interfaceType="Edit"
          loading={upsertingBin}
        />
      </Dialog>
    </Grid>
  );
};

export default MetalBins;
